import React, { useState, useEffect } from 'react';
import { Users, Search, Plus, Edit, Trash2, Mail, Phone, CheckCircle, XCircle, Download, Upload, Filter, MoreVertical, UserCheck, UserX, Shield, Eye, AlertCircle, Calendar, Briefcase, MapPin } from 'lucide-react';
import { userManagementController } from '../../controllers/userManagementController';
import { authController } from '../../controllers/authController';

const UserManagementPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', role: 'employee', department: '', designation: '', status: 'active'
  });

  useEffect(() => {
    setCurrentUser(authController.getCurrentUser());
    loadUsers();
  }, []);

  const loadUsers = () => {
    setUsers(userManagementController.getAllUsers());
  };

  const stats = userManagementController.getStatistics();

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleCreate = () => {
    setModalMode('create');
    setFormData({ name: '', email: '', phone: '', role: 'employee', department: '', designation: '', status: 'active' });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      department: user.department,
      designation: user.designation,
      status: user.status
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (modalMode === 'create') {
      userManagementController.createUser(formData);
    } else {
      userManagementController.updateUser(selectedUser.id, formData);
    }
    loadUsers();
    setShowModal(false);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    userManagementController.deleteUser(selectedUser.id);
    loadUsers();
    setShowDeleteConfirm(false);
  };

  const handleToggleStatus = (userId) => {
    userManagementController.toggleUserStatus(userId);
    loadUsers();
  };

  const handleExport = () => {
    const json = userManagementController.exportUsers();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString()}.json`;
    a.click();
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: { bg: '#fef3c7', text: '#92400e' },
      admin: { bg: '#dbeafe', text: '#1e40af' },
      manager: { bg: '#e9d5ff', text: '#6b21a8' },
      employee: { bg: '#d1fae5', text: '#065f46' }
    };
    return colors[role] || colors.employee;
  };

  const s = {
    container: { padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    toolbar: { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    searchBox: { position: 'relative', marginBottom: '16px' },
    searchInput: { width: '100%', padding: '12px 12px 12px 44px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' },
    filters: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' },
    select: { padding: '10px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
    actions: { display: 'flex', gap: '12px' },
    button: { padding: '10px 20px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
    primaryButton: { backgroundColor: '#3b82f6', color: '#fff' },
    table: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    thead: { backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' },
    th: { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginTop: '24px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    formGroupFull: { gridColumn: '1 / -1' },
    label: { fontSize: '14px', fontWeight: '600', color: '#1e293b' },
    input: { padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' },
    userAvatar: { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 'bold', color: '#64748b' }
  };

  if (!currentUser || (currentUser.role !== 'super_admin' && currentUser.role !== 'admin')) {
    return (
      <div style={s.container}>
        <div style={{ backgroundColor: '#fee2e2', border: '2px solid #ef4444', borderRadius: '12px', padding: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AlertCircle size={32} color="#dc2626" />
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#991b1b' }}>Access Denied</h3>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#7f1d1d' }}>Only Admins can manage users.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <h1 style={s.title}>
        <Users size={36} color="#3b82f6" />
        User Management
      </h1>
      <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '32px' }}>Manage users, roles, and permissions</p>

      <div style={s.statsGrid}>
        {[
          { label: 'Total Users', value: stats.total, icon: Users, color: '#1e293b' },
          { label: 'Active', value: stats.active, icon: UserCheck, color: '#10b981' },
          { label: 'Inactive', value: stats.inactive, icon: UserX, color: '#ef4444' },
          { label: 'Verified', value: stats.verified, icon: CheckCircle, color: '#3b82f6' }
        ].map((stat, idx) => (
          <div key={idx} style={s.statCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <stat.icon size={24} color={stat.color} />
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>{stat.label}</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div style={s.toolbar}>
        <div style={s.searchBox}>
          <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input type="text" style={s.searchInput} placeholder="Search users by name or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div style={s.filters}>
          <select style={s.select} value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
          </select>
          <select style={s.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div style={s.actions}>
          <button style={{ ...s.button, ...s.primaryButton }} onClick={handleCreate}>
            <Plus size={16} /> Add User
          </button>
          <button style={{ ...s.button, backgroundColor: '#10b981', color: '#fff' }} onClick={handleExport}>
            <Download size={16} /> Export
          </button>
        </div>
      </div>

      <div style={s.table}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={s.thead}>
            <tr>
              <th style={s.th}>User</th>
              <th style={s.th}>Contact</th>
              <th style={s.th}>Role</th>
              <th style={s.th}>Department</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Last Login</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const roleColor = getRoleBadgeColor(user.role);
              return (
                <tr key={user.id}>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={s.userAvatar}>
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>{user.name}</div>
                        <div style={{ fontSize: '13px', color: '#64748b' }}>{user.designation || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={s.td}>
                    <div style={{ fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <Mail size={14} color="#64748b" />
                        <span>{user.email}</span>
                      </div>
                      {user.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b' }}>
                          <Phone size={14} />
                          <span>{user.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, backgroundColor: roleColor.bg, color: roleColor.text }}>
                      {user.role.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: '14px', color: '#1e293b' }}>{user.department || 'N/A'}</span>
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {user.status === 'active' ? (
                        <>
                          <CheckCircle size={16} color="#10b981" />
                          <span style={{ color: '#10b981', fontSize: '14px', fontWeight: '500' }}>Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={16} color="#ef4444" />
                          <span style={{ color: '#ef4444', fontSize: '14px', fontWeight: '500' }}>Inactive</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td style={s.td}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        style={{ padding: '6px', border: 'none', borderRadius: '6px', backgroundColor: '#dbeafe', color: '#1e40af', cursor: 'pointer' }}
                        onClick={() => handleEdit(user)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        style={{ padding: '6px', border: 'none', borderRadius: '6px', backgroundColor: user.status === 'active' ? '#fee2e2' : '#dcfce7', color: user.status === 'active' ? '#dc2626' : '#16a34a', cursor: 'pointer' }}
                        onClick={() => handleToggleStatus(user.id)}
                        title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        {user.status === 'active' ? <UserX size={16} /> : <UserCheck size={16} />}
                      </button>
                      {currentUser.role === 'super_admin' && (
                        <button
                          style={{ padding: '6px', border: 'none', borderRadius: '6px', backgroundColor: '#fee2e2', color: '#dc2626', cursor: 'pointer' }}
                          onClick={() => handleDelete(user)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={s.modal} onClick={() => setShowModal(false)}>
          <div style={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px' }}>
              {modalMode === 'create' ? 'Add New User' : 'Edit User'}
            </h2>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
              {modalMode === 'create' ? 'Create a new user account' : 'Update user information'}
            </p>

            <div style={s.formGrid}>
              <div style={{ ...s.formGroup, ...s.formGroupFull }}>
                <label style={s.label}>Full Name *</label>
                <input type="text" style={s.input} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter full name" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Email *</label>
                <input type="email" style={s.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="user@example.com" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Phone</label>
                <input type="tel" style={s.input} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 1234567890" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Role *</label>
                <select style={s.input} value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  {currentUser.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Department</label>
                <select style={s.input} value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })}>
                  <option value="">Select Department</option>
                  <option value="Sales">Sales</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Engineering">Engineering</option>
                  <option value="HR">HR</option>
                  <option value="Finance">Finance</option>
                  <option value="Operations">Operations</option>
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Designation</label>
                <input type="text" style={s.input} value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} placeholder="Enter designation" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Status</label>
                <select style={s.input} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '2px solid #e2e8f0' }}>
              <button style={{ flex: 1, padding: '12px', backgroundColor: '#e2e8f0', color: '#1e293b', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }} onClick={handleSave}>
                {modalMode === 'create' ? 'Create User' : 'Update User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div style={s.modal} onClick={() => setShowDeleteConfirm(false)}>
          <div style={{ ...s.modalContent, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <AlertCircle size={32} color="#dc2626" />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px' }}>Delete User</h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px' }}>
                Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button style={{ flex: 1, padding: '12px', backgroundColor: '#e2e8f0', color: '#1e293b', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button style={{ flex: 1, padding: '12px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }} onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;